import { Dayjs } from 'dayjs';
import { DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { BusinessLevelMarketingSubscriptionType } from './business-level-marketing-subscription-type.js';
import { BusinessLevelMarketingSubscription } from './business-level-marketing-subscription.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { TimeZone } from '../../../time/timezone.js';
import { cloneData, isSameData } from '../../dto/data-model-utils.js';

export class PersistenceOnlySubscription extends BusinessLevelMarketingSubscription {
  static readonly TYPE_ID: string = 'c44451fd628b400cb859cd05b007c448';

  private constructor(
    public readonly emailAddress: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly consentDate: Dayjs,
    public readonly subscriptionDate: Dayjs,
  ) {
    super();
  }

  get $typeId(): string {
    return PersistenceOnlySubscription.TYPE_ID;
  }

  get type(): BusinessLevelMarketingSubscriptionType {
    return BusinessLevelMarketingSubscriptionType.PersistenceOnly;
  }

  static create(args: {
    emailAddress: string,
    businessRef: BusinessModuleRef,
    consentDate: Dayjs,
    subscriptionDate: Dayjs,
  }): PersistenceOnlySubscription {
    return new PersistenceOnlySubscription(
        args.emailAddress,
        args.businessRef,
        args.consentDate,
        args.subscriptionDate,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): PersistenceOnlySubscription {
    return this.create({
      emailAddress: MapUtils.getString(map, 'emailAddress'),
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      consentDate: context.decodeDate(MapUtils.getNumber(map, 'consentDate')),
      subscriptionDate: context.decodeDate(MapUtils.getNumber(map, 'subscriptionDate')),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      emailAddress: this.emailAddress,
      businessRef: this.businessRef.encode(),
      consentDate: context.encodeDate(this.consentDate),
      subscriptionDate: context.encodeDate(this.subscriptionDate),
    };
  }

  clone(): this {
    return PersistenceOnlySubscription.create({
      emailAddress: this.emailAddress,
      businessRef: cloneData(this.businessRef),
      consentDate: TimeZone.cloneDate(this.consentDate),
      subscriptionDate: TimeZone.cloneDate(this.subscriptionDate),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return other.emailAddress === this.emailAddress &&
      isSameData(other.businessRef, this.businessRef) &&
      other.consentDate === this.consentDate &&
      other.subscriptionDate === this.subscriptionDate;
  }
}

export class PersistenceOnlySubscriptionBuilder extends DataModelBuilder<PersistenceOnlySubscription> {
  build(context: SerializationContext, map: SerializedData): PersistenceOnlySubscription {
    return PersistenceOnlySubscription.fromMap(context, map);
  }
}
